/**
 * DriverLayout - Dedicated layout shell for driver role
 *
 * Replaces the full fleet Layout when user.role === 'driver'.
 * Provides a slim sidebar with only driver-relevant navigation items
 * and a compact header with driver name, vehicle plate, and logout.
 *
 * Mobile: sidebar is hidden by default, toggled via hamburger button.
 * Desktop (md+): sidebar is always visible.
 *
 * Badge counts on sidebar items show pending missions.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Home, Map, Car, Fuel, Scale, FileText, User, LogOut, ChevronDown, AlertTriangle, Menu, X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.jsx';
import { driverMonoAPI } from '../../../api/missions.js';

const DRIVER_NAV_ITEMS = [
  { id: 'home', label: 'Accueil', icon: Home },
  { id: 'missions', label: 'Mes Missions', icon: Map, badgeKey: 'missions_pending' },
  { id: 'vehicle', label: 'Mon Véhicule', icon: Car },
  { id: 'fuel', label: 'Mon Carburant', icon: Fuel },
  { id: 'report', label: 'Signaler', icon: AlertTriangle },
  { id: 'fines', label: 'Mes Amendes', icon: Scale },
  { id: 'documents', label: 'Mes Documents', icon: FileText },
  { id: 'profile', label: 'Mon Profil', icon: User },
];

function DriverSidebarItem({ item, isActive, onClick, badge }) {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onClick(item.id)}
      className={`
        w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm mb-0.5
        transition-all
        ${isActive
          ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
          : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-slate-800/50'
        }
      `}
    >
      <Icon size={16} />
      <span className="flex-1 text-left">{item.label}</span>
      {badge > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

export default function DriverLayout({ activeTab, onTabChange, children }) {
  const { user, logout } = useAuth();
  const [driverInfo, setDriverInfo] = useState(null);
  const [badges, setBadges] = useState({});
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userMenuRef = useRef(null);
  const sidebarRef = useRef(null);

  // Load driver profile + stats for header and badges
  useEffect(() => {
    const loadDriverData = async () => {
      try {
        const [profileRes, statsRes] = await Promise.all([
          driverMonoAPI.getProfile(),
          driverMonoAPI.getStats()
        ]);
        if (profileRes.data) setDriverInfo(profileRes.data);
        if (statsRes.data) {
          // Count missions needing action (assigned = needs accept, accepted = can start)
          const pending = (statsRes.data.missions_active || 0);
          setBadges({ missions_pending: pending });
        }
      } catch (err) {
        console.error('Failed to load driver data:', err);
      }
    };
    loadDriverData();
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile sidebar on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const driverName = driverInfo?.driver
    ? `${driverInfo.driver.first_name || ''} ${driverInfo.driver.last_name || ''}`.trim()
    : user?.first_name || user?.email || '';

  const vehiclePlate = driverInfo?.vehicle?.plate_number || null;

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    setSidebarOpen(false); // Auto-close on mobile
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-slate-950 text-gray-900 dark:text-white flex overflow-hidden">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 z-40 w-56
          bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800
          flex flex-col
          transform transition-transform duration-200 ease-in-out
          md:relative md:translate-x-0 md:w-52
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo + mobile close */}
        <div className="p-3 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="FleetManager" className="w-8 h-8 rounded-lg" />
            <div className="flex-1">
              <div className="font-bold text-gray-900 dark:text-white text-sm">FleetManager</div>
              <div className="text-xs text-gray-500 dark:text-slate-500">Conducteur</div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={18} className="text-gray-500 dark:text-slate-400" />
            </button>
          </div>
        </div>

        {/* Driver badge */}
        <div className="px-3 py-2">
          <div
            className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50"
            style={{ borderLeftColor: '#3b82f6', borderLeftWidth: '3px' }}
          >
            <div className="text-xs text-gray-900 dark:text-white font-medium truncate">{driverName}</div>
            {vehiclePlate && (
              <div className="text-xs text-gray-500 dark:text-slate-500 flex items-center gap-1 mt-0.5">
                <Car size={10} />
                {vehiclePlate}
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {DRIVER_NAV_ITEMS.map(item => (
            <DriverSidebarItem
              key={item.id}
              item={item}
              isActive={activeTab === item.id}
              onClick={handleTabChange}
              badge={item.badgeKey ? badges[item.badgeKey] : 0}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 dark:border-slate-800">
          <div className="text-xs text-gray-500 dark:text-slate-500 text-center">
            FleetManager Max
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Slim header */}
        <header className="h-12 bg-white dark:bg-slate-900/80 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {/* Hamburger - mobile only */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              <Menu size={20} className="text-gray-600 dark:text-slate-400" />
            </button>
            <div className="text-sm text-gray-500 dark:text-slate-400">
              {DRIVER_NAV_ITEMS.find(i => i.id === activeTab)?.label || 'Accueil'}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* User menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
                  {driverName.charAt(0) || '?'}
                </div>
                <span className="hidden sm:inline text-sm text-gray-700 dark:text-slate-300 max-w-[120px] truncate">{driverName}</span>
                <ChevronDown size={12} className="text-gray-500 dark:text-slate-400" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-slate-700">
                    <div className="text-sm text-gray-900 dark:text-white font-medium truncate">{driverName}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{user?.email}</div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut size={16} />
                      <span className="text-sm">Déconnexion</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-gray-100 dark:bg-slate-900">
          <div className="max-w-6xl mx-auto px-4 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
